import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Language types
export type Language = 'en' | 'th';
export type TranslationKey = string;

// Translation data structure
type Translations = {
  [key: string]: any;
};

// Context interface
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, params?: Record<string, any>) => string;
  isLoading: boolean;
}

// Create context
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Language provider props
interface LanguageProviderProps {
  children: ReactNode;
  defaultLanguage?: Language;
}

// Translation cache
const translationCache: Record<Language, Translations> = {
  en: {},
  th: {}
};

// Load translations dynamically
async function loadTranslations(language: Language): Promise<Translations> {
  if (translationCache[language] && Object.keys(translationCache[language]).length > 0) {
    return translationCache[language];
  }

  try {
    const response = await import(`../locales/${language}.json`);
    translationCache[language] = response.default || response;
    return translationCache[language];
  } catch (error) {
    console.error(`Failed to load translations for ${language}:`, error);
    // Fallback to English if loading fails
    if (language !== 'en') {
      return loadTranslations('en');
    }
    return {};
  }
}

// Get nested translation value
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : undefined;
  }, obj);
}

// Language provider component
export function LanguageProvider({ children, defaultLanguage = 'en' }: LanguageProviderProps) {
  const [language, setLanguageState] = useState<Language>(defaultLanguage);
  const [translations, setTranslations] = useState<Translations>({});
  const [isLoading, setIsLoading] = useState(true);

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('aiya-beacon-language') as Language;
    if (savedLanguage && (savedLanguage === 'en' || savedLanguage === 'th')) {
      setLanguageState(savedLanguage);
    }
  }, []);

  // Load translations when language changes
  useEffect(() => {
    let isMounted = true;
    
    async function loadCurrentTranslations() {
      setIsLoading(true);
      try {
        const newTranslations = await loadTranslations(language);
        if (isMounted) {
          setTranslations(newTranslations);
        }
      } catch (error) {
        console.error('Error loading translations:', error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadCurrentTranslations();

    return () => {
      isMounted = false;
    };
  }, [language]);

  // Set language and persist to localStorage
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('aiya-beacon-language', lang);
  };

  // Translation function with parameter support
  const t = (key: TranslationKey, params?: Record<string, any>): string => {
    const value = getNestedValue(translations, key);
    
    if (value === undefined) {
      console.warn(`Translation missing for key: ${key} in language: ${language}`);
      return key; // Return the key itself as fallback
    }

    if (typeof value !== 'string') {
      console.warn(`Translation value for key: ${key} is not a string`);
      return String(value);
    }

    // Replace parameters in the translation string
    if (params) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, paramKey) => {
        return params[paramKey] !== undefined ? String(params[paramKey]) : match;
      });
    }

    return value;
  };

  const contextValue: LanguageContextType = {
    language,
    setLanguage,
    t,
    isLoading
  };

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

// Hook to use language context
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

// Hook specifically for translation function
export function useTranslation() {
  const { t, language, isLoading } = useLanguage();
  return { t, language, isLoading };
}

// Utility function for formatting currency
export function formatCurrency(amount: number, language: Language = 'en'): string {
  const locale = language === 'th' ? 'th-TH' : 'en-US';
  const currency = 'THB';
  
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

// Utility function for formatting dates
export function formatDate(date: string | Date, language: Language = 'en', includeTime = false): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const locale = language === 'th' ? 'th-TH' : 'en-US';
  
  const options: Intl.DateTimeFormatOptions = includeTime 
    ? { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }
    : { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit' 
      };

  return new Intl.DateTimeFormat(locale, options).format(dateObj);
}